import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const NetworkStatusContext = createContext(null);

// Tracks browser connectivity via the Network Information events. `isOnline`
// starts from navigator.onLine and flips on the window online/offline events.
// Consumers use this to show the offline banner and to disable actions that
// require the API while the connection is down.
export function NetworkStatusProvider({ children }) {
  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Sync once on mount in case the state changed before listeners attached.
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const value = useMemo(() => ({ isOnline }), [isOnline]);

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
    </NetworkStatusContext.Provider>
  );
}

export function useNetworkStatus() {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error("useNetworkStatus must be used within a NetworkStatusProvider");
  }
  return context;
}
